import { useQuery } from '@tanstack/react-query'
import { TrendingUp } from 'lucide-react'
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { accountApi } from '../api/client'
import { ChartCard } from './ChartCard'
import { useTimeframe } from '../hooks/useTimeframe'
import { timeframeLabel, toEquityRange } from '../lib/timeframe'
import { fmtUsd } from './desk/fleetSummaries'
import './BookValueChart.css'
// `.chart-fill` (BookValueLine's fill mode, the Book desk's hero chart) is defined in THIS stylesheet, shared with
// FleetValueChart's fill mode. Vitest ignores CSS, so renaming or moving that rule would collapse the hero to 0
// height with every test still green: keep this import, or move the rule and update both importers.
import './FleetValueOverlay.css'

interface BookValueChartProps {
  accountId: number
}

interface BookValueLineProps {
  /** One point per day; `equity` is already a number (the caller applies Number() to the wire string). */
  rows: Array<{ t: string; equity: number }>
  /**
   * Size to the parent flex cell (height 100%) instead of the fixed 220px. Fill is also the hero
   * chart's mode: its Y axis follows the data and prints whole dollars (the 220px card keeps the
   * $0-based, k-scale axis it always had).
   */
  fill?: boolean
  /**
   * The fill chart's text alternative: with it the `.chart-fill` wrapper is `role="img"` and carries
   * this label. Ignored without `fill`, whose DOM has no wrapper and stays exactly as it was.
   */
  ariaLabel?: string
}

// Short form for the Y axis, e.g. 21675.74 -> "$21.7k". Values below $1k
// print in full dollars — a book fresh off inception won't have a k-scale
// equity yet.
const fmtShort = (v: number): string =>
  Math.abs(v) >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`

const fmtUSD = (v: number): string =>
  `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// The fill chart's Y axis labels are whole dollars ("$24,013"), so its width is worked out from them.
/** recharts' own YAxis width. A typical book's labels (about 40px at 11px) fit it, so they keep today's geometry. */
const AXIS_MIN_WIDTH = 60
/** A generous width for one character of an 11px Geist label: digits run 5.9-6.6px, "$" 8px, "," 3px. */
const AXIS_CHAR_PX = 6.5
/** What the axis spends beside its labels: the tick line (6px), the gap before it (2px) and a cushion. */
const AXIS_TICK_PX = 12

/**
 * The width the fill chart's Y axis needs for its longest tick label, never less than recharts' 60px
 * (a $1.2M book's "$1,234,567" is 54px and needs more). The auto domain rounds out to "nice" ticks
 * that can run past a power of ten (99,100 up to 100,000), so the label measured is the one for a
 * value 10% above the largest equity.
 */
function axisWidth(rows: BookValueLineProps['rows']): number {
  const top = rows.reduce((max, r) => (Number.isFinite(r.equity) ? Math.max(max, Math.abs(r.equity)) : max), 0)
  const chars = fmtUsd(Math.ceil(top * 1.1)).length
  return Math.max(AXIS_MIN_WIDTH, Math.ceil(chars * AXIS_CHAR_PX + AXIS_TICK_PX))
}

/**
 * The equity line on its own: the responsive container and the line chart, with
 * no card shell and no loading ladder. `BookValueChart` wraps it at a fixed
 * 220px; the Book desk's `book-value` module draws it with `fill`, which sizes
 * it to the module's body (the same extraction as `FleetValueChart`).
 *
 * The two modes differ in the Y axis. The 220px card is exactly as it was: no
 * domain (recharts starts at $0) and k-scale ticks. The hero chart exists to
 * show the trend, and on a $0 base a $24k book moving $400 in a week is a 2px
 * wiggle, so its axis follows the data (`auto`), steps in whole dollars
 * (`allowDecimals={false}`, so two ticks can never print the same label) and
 * prints them as dollars with a thousands separator, on an axis as wide as the
 * longest of them.
 */
export function BookValueLine({ rows, fill = false, ariaLabel }: BookValueLineProps) {
  const chart = (
    <ResponsiveContainer width="100%" height={fill ? '100%' : 220}>
      <LineChart data={rows}>
        <XAxis dataKey="t" stroke="var(--fg-subtle)" fontSize={11} />
        {fill ? (
          <YAxis
            stroke="var(--fg-subtle)"
            fontSize={11}
            domain={['auto', 'auto']}
            allowDecimals={false}
            width={axisWidth(rows)}
            tickFormatter={(v: number) => fmtUsd(v)}
          />
        ) : (
          <YAxis stroke="var(--fg-subtle)" fontSize={11} tickFormatter={(v: number) => fmtShort(v)} />
        )}
        <Tooltip formatter={(v: number) => fmtUSD(v)} />
        <Line type="monotone" dataKey="equity" stroke="var(--brand)" strokeWidth={1.5} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  )
  if (!fill) return chart
  return (
    <div className="chart-fill" role={ariaLabel ? 'img' : undefined} aria-label={ariaLabel || undefined}>
      {chart}
    </div>
  )
}

/**
 * Per-book equity trend line: `PairedExcessChart`'s card shell + three-state
 * ladder (P8), ranged by the global timeframe from `useTimeframe()` (no
 * local range toggle). Deliberately does NOT poll (staleTime: 300000, no
 * refetchInterval, §2.4) — a daily-granularity series doesn't need 30s
 * freshness, and polling three fleet charts would hammer the broker's rate
 * limit.
 */
export function BookValueChart({ accountId }: BookValueChartProps) {
  const { timeframe } = useTimeframe()
  const range = toEquityRange(timeframe)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['equity-history', accountId, range],
    queryFn: () => accountApi.history(accountId, range),
    retry: 1,
    staleTime: 300000,
  })

  // equity arrives as a decimal STRING (json_encoders = {Decimal: str});
  // Number() is applied here, at the chart boundary, and nowhere earlier.
  const rows = (data?.points ?? []).map((p) => ({ t: p.t, equity: Number(p.equity) }))

  return (
    <ChartCard
      className="book-value-chart"
      title={
        <>
          <TrendingUp size={18} className="icon" aria-hidden="true" />
          Book value
        </>
      }
      headerExtra={
        <span className="caption">
          {timeframe === 'DAY' ? 'showing 1W · day view' : `window ${timeframeLabel(timeframe)}`}
        </span>
      }
      isLoading={isLoading}
      isError={isError}
      errorText="chart unavailable"
      isEmpty={rows.length < 2}
      emptyText="no equity history yet"
      emptyIcon={<TrendingUp size={28} aria-hidden="true" />}
    >
      <BookValueLine rows={rows} />
    </ChartCard>
  )
}
